import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import s from './Misc.module.css';
import { Icon } from './Icon';
import { Button } from './Button';
import { initials } from '@/lib/format';

export function PageHeader({ title, subtitle, actions, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <header className={s.pageHeader}>
      <div className={s.phTitles}>
        {back ? <Link to={back.to} className={s.back}><Icon name="arrowLeft" size={14} />{back.label}</Link> : null}
        <h1>{title}</h1>
        {subtitle ? <div className={s.phSub}>{subtitle}</div> : null}
      </div>
      {actions ? <div className={s.phActions}>{actions}</div> : null}
    </header>
  );
}

export function Stat({ label, value, sub, icon, trend }: { label: ReactNode; value: ReactNode; sub?: ReactNode; icon?: string; trend?: number | null }) {
  return (
    <div className={s.stat}>
      <div className={s.statLabel}>{icon ? <Icon name={icon} size={14} /> : null}{label}</div>
      <div className={s.statValue}>{value}</div>
      {sub != null || trend != null ? (
        <div className={s.statSub}>
          {trend != null && Number.isFinite(trend) ? <span className={trend >= 0 ? s.trendUp : s.trendDown}>{trend >= 0 ? '▲' : '▼'} {Math.abs(Math.round(trend * 100))}% </span> : null}
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export function Meter({ value, max, tone, label }: { value: number; max: number; tone?: 'ok' | 'warn' | 'bad' | 'promo'; label?: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const t = tone ?? (pct >= 95 ? 'bad' : pct >= 80 ? 'warn' : undefined);
  return (
    <div className={s.meter} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <div className={[s.meterFill, t && s[t]].filter(Boolean).join(' ')} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Tabs<V extends string>({ value, onChange, items }: { value: V; onChange: (v: V) => void; items: { value: V; label: ReactNode; count?: number }[] }) {
  return (
    <div className={s.tabs} role="tablist">
      {items.map((it) => (
        <button key={it.value} role="tab" type="button" className={s.tab} aria-selected={it.value === value} onClick={() => onChange(it.value)}>
          {it.label}{it.count != null ? <span className={s.tabCount}>{it.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Route-backed tabs (sub-navigation). */
export function NavTabs({ items }: { items: { to: string; label: ReactNode; end?: boolean }[] }) {
  return (
    <nav className={s.tabs} aria-label="Section">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} className={s.tab}>{it.label}</NavLink>
      ))}
    </nav>
  );
}

export function Avatar({ name, src, size = 30 }: { name?: string | null; src?: string | null; size?: number }) {
  return (
    <span className={s.avatar} style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }} aria-hidden>
      {src ? <img src={src} alt="" /> : initials(name)}
    </span>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className={s.kv}>
      {items.map(([k, v], i) => (<div key={i} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v}</dd></div>))}
    </dl>
  );
}

export function CopyBox({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={s.copyBox}>
      <code>{value}</code>
      <Button size="sm" icon={copied ? 'check' : 'copy'} onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); }).catch(() => undefined);
      }}>{copied ? 'Copied' : 'Copy'}</Button>
    </div>
  );
}

export function Steps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className={s.steps} aria-label="Progress" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {steps.map((label, i) => (
        <li key={label} style={{ display: 'contents' }}>
          {i > 0 ? <span className={s.stepSep} aria-hidden /> : null}
          <span className={s.step} data-state={i < current ? 'done' : i === current ? 'current' : 'todo'} aria-current={i === current ? 'step' : undefined}>
            <span className={s.stepDot}>{i < current ? <Icon name="check" size={12} strokeWidth={3} /> : i + 1}</span>
            <span className={s.stepLabel}>{label}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
